// Helper functions for form handlers

/**
 * Handle hotel input change
 * @param {Object} e - The event object
 * @param {Function} setHotelFormData - State setter for hotel form data
 * @param {Object} hotelFormData - Current hotel form data
 * @param {Function} setFormErrors - State setter for form errors
 * @param {Object} formErrors - Current form errors for hotel
 */
export const handleHotelInputChange = (e, setHotelFormData, hotelFormData, setFormErrors, formErrors) => {
  const { name, value, type, checked } = e.target;
  
  if (type === 'checkbox') {
    setHotelFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  } else {
    setHotelFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }
  
  if (formErrors[name]) {
    setFormErrors(prev => ({
      ...prev,
      hotel: {
        ...prev.hotel,
        [name]: ''
      }
    }));
  }
};

/**
 * Handle room input change
 * @param {Object} e - The event object
 * @param {Function} setRoomFormData - State setter for room form data
 * @param {Function} setFormErrors - State setter for form errors
 * @param {Object} formErrors - Current form errors
 */
export const handleRoomInputChange = (e, setRoomFormData, setFormErrors, formErrors) => {
  const { name, value } = e.target;
  setRoomFormData(prev => ({
    ...prev,
    [name]: value
  }));
  
  if (formErrors.room[name]) {
    setFormErrors(prev => ({
      ...prev,
      room: {
        ...prev.room,
        [name]: ''
      }
    }));
  }
};

/**
 * Handle Travelomatix room input change
 * @param {Object} e - The event object
 * @param {Function} setTravelomatixRoomFormData - State setter for Travelomatix room form data
 * @param {Function} setTravelomatixFormErrors - State setter for Travelomatix form errors
 * @param {Object} travelomatixFormErrors - Current Travelomatix form errors
 */
export const handleTravelomatixRoomInputChange = (e, setTravelomatixRoomFormData, setTravelomatixFormErrors, travelomatixFormErrors) => {
  const { name, value, type, checked } = e.target;
  
  if (type === 'checkbox') {
    if (name.startsWith('accessibility_features.')) {
      const featureName = name.split('.')[1];
      setTravelomatixRoomFormData(prev => ({
        ...prev,
        accessibility_features: {
          ...prev.accessibility_features,
          [featureName]: checked
        }
      }));
    }
  } else {
    setTravelomatixRoomFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }
  
  // Clear error when user starts typing
  if (travelomatixFormErrors[name]) {
    setTravelomatixFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  }
};

/**
 * Handle room search change
 * @param {Object} e - The event object
 * @param {Function} setRoomSearchParams - State setter for room search params
 */
export const handleRoomSearchChange = (e, setRoomSearchParams) => {
  const { name, value } = e.target;
  setRoomSearchParams(prev => ({
    ...prev,
    [name]: value
  }));
};

/**
 * Handle image file change for hotels
 * @param {Object} e - The event object
 * @param {Function} setHotelFormData - State setter for hotel form data
 */
export const handleHotelImageChange = (e, setHotelFormData) => {
  const file = e.target.files[0];
  if (file) {
    setHotelFormData(prev => ({
      ...prev,
      imageFile: file,
      image_url: URL.createObjectURL(file) // Preview the image
    }));
  }
};

/**
 * Handle image file change for rooms
 * @param {Object} e - The event object
 * @param {Function} setRoomFormData - State setter for room form data
 */
export const handleRoomImageChange = (e, setRoomFormData) => {
  const file = e.target.files[0];
  if (file) {
    setRoomFormData(prev => ({
      ...prev,
      imageFile: file,
      image_url: URL.createObjectURL(file) // Preview the image
    }));
  }
};

/**
 * Reset hotel form
 * @param {Function} setHotelFormData - State setter for hotel form data
 * @param {Function} setEditingHotel - State setter for editing hotel
 * @param {Function} setShowHotelForm - State setter for showing hotel form
 * @param {Function} setErrors - State setter for errors
 * @param {Function} setFormErrors - State setter for form errors
 */
export const resetHotelForm = (setHotelFormData, setEditingHotel, setShowHotelForm, setErrors, setFormErrors) => {
  setHotelFormData({
    name_en: '',
    featured: false,
    city: '',
    country: '',
    images_urls: [''],
    supplier_id: '',
    commission_percentage: 10,
    // Add accessibility template IDs
    accessibility_template_ids: []
  });
  setEditingHotel(null);
  setShowHotelForm(false);
  setErrors(prev => ({ ...prev, hotelForm: null }));
  setFormErrors(prev => ({ ...prev, hotel: {} }));
};

/**
 * Reset room form
 * @param {Function} setRoomFormData - State setter for room form data
 * @param {Function} setEditingRoom - State setter for editing room
 * @param {Function} setShowRoomForm - State setter for showing room form
 * @param {Function} setErrors - State setter for errors
 * @param {Function} setFormErrors - State setter for form errors
 */
export const resetRoomForm = (setRoomFormData, setEditingRoom, setShowRoomForm, setErrors, setFormErrors) => {
  setRoomFormData({
    hotel_id: '',
    room_type: '',
    name_en: '',
    description_en: '',
    price_per_night: '',
    capacity: 1,
    currency: 'USD',
    image_url: '',
    imageFile: null
  });
  setEditingRoom(null);
  setShowRoomForm(false);
  setErrors(prev => ({ ...prev, roomForm: null }));
  setFormErrors(prev => ({ ...prev, room: {} }));
};

/**
 * Reset Travelomatix room form
 * @param {Function} setShowTravelomatixRoomForm - State setter for showing Travelomatix room form
 * @param {Function} setTravelomatixRoomData - State setter for Travelomatix room data
 * @param {Function} setRoomSearchError - State setter for room search error
 * @param {Function} setTravelomatixRoomFormData - State setter for Travelomatix room form data
 */
export const resetTravelomatixRoomForm = (setShowTravelomatixRoomForm, setTravelomatixRoomData, setRoomSearchError, setTravelomatixRoomFormData) => {
  setShowTravelomatixRoomForm(false);
  setTravelomatixRoomData(null);
  setRoomSearchError(null);
  setTravelomatixRoomFormData({
    hotel_id: '',
    name_en: '',
    name_ar: '',
    supplier_id: '',
    room_type: '',
    // description_en: '',
    // description_ar: '',
    capacity: 1,
    price_per_night: '',
    currency: 'USD',
    images_urls: [''],
    // Add accessibility template IDs
    accessibility_template_ids: []
  });
};

/**
 * Reset Travelomatix room form and edit state
 * @param {Function} resetTravelomatixRoomForm - Function to reset Travelomatix room form
 * @param {Function} setEditingRoom - State setter for editing room
 */
export const resetTravelomatixRoomFormAndEdit = (resetTravelomatixRoomForm, setEditingRoom) => {
  resetTravelomatixRoomForm();
  setEditingRoom(null);
};

/**
 * Reset room search form
 * @param {Function} setRoomSearchParams - State setter for room search params
 * @param {Function} setRoomSearchError - State setter for room search error
 */
export const resetRoomSearchForm = (setRoomSearchParams, setRoomSearchError) => {
  setRoomSearchParams({
    resultToken: '',
    checkInDate: '',
    checkOutDate: '',
    guestNationality: 'SA',
    noOfRooms: 1,
    noOfNights: 1,
    adults: 2,
    children: 0,
    childAges: []
  });
  setRoomSearchError(null);
};

export default {
  handleHotelInputChange,
  handleRoomInputChange,
  handleTravelomatixRoomInputChange,
  handleRoomSearchChange,
  handleHotelImageChange,
  handleRoomImageChange,
  resetHotelForm,
  resetRoomForm,
  resetTravelomatixRoomForm,
  resetTravelomatixRoomFormAndEdit,
  resetRoomSearchForm
};